import { useEffect, useRef } from 'react';

type UseCourseVideoProgressIntervalParams = {
  enabled: boolean;
  unlocked: boolean | undefined;
  watchedSection: number;
  getVideoElement: () => HTMLVideoElement | null;
  onProgressTick: (currentSection: number, currentTime: number) => void;
};

const PROGRESS_INTERVAL_MS = 5000;

export function useCourseVideoProgressInterval({
  enabled,
  unlocked,
  watchedSection,
  getVideoElement,
  onProgressTick,
}: UseCourseVideoProgressIntervalParams) {
  const lastSentTimeRef = useRef<number | null>(null);

  useEffect(() => {
    lastSentTimeRef.current = null;
  }, [watchedSection]);

  useEffect(() => {
    if (!enabled) return;

    const intervalId = setInterval(() => {
      if (unlocked === false) return;

      const video = getVideoElement();
      if (!video || video.paused) return;

      const currentTime = Math.max(0, Math.floor(video.currentTime));
      if (lastSentTimeRef.current === currentTime) return;

      lastSentTimeRef.current = currentTime;
      onProgressTick(watchedSection, currentTime);
    }, PROGRESS_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [enabled, unlocked, watchedSection, getVideoElement, onProgressTick]);
}
